import React from 'react';
import { cx } from '../../../lib/styles';

// Props interface for the Loader component
export interface SkeletonLoaderProps {
  loaderType: 'Article' | 'TileBlock' | 'FeatureTileBlock';
  config?: {
    [key: string]: string; // Allows for custom styles to be passed
  },
  cssClasses?: string; // Optional CSS classes for additional styling
}

/**
 * Skeleton Loader component to display loading states for different types of content.
 * This component renders different skeleton loaders based on the `loaderType` prop.
 * @param loaderType - The type of loader to render. This prop will determine which skeleton loader to display.
 * @returns JSX element representing the skeleton loader.
 * @link CSS is stored in `src/lib/baseCSS.ts`
 */
const SkeletonLoader: React.FC<SkeletonLoaderProps> = ({ 
  loaderType, 
  config = {
    width: '100% !important', 
    height: 'auto !important', 
  },
  cssClasses = ''
}) => {
  const renderArticleLoader = () => (
    <div className={cx(cssClasses, 'loader-wrapper responsive article-loader-container')} style={{ ...config }}>
      <div className="skeleton-line article-title-line"></div>
      <div className="skeleton-line article-text-line width-90"></div>
      <div className="skeleton-line article-text-line width-95"></div>
      <div className="skeleton-line article-text-line width-80"></div>
      <div className="skeleton-line article-text-line width-70"></div>
      <div className="skeleton-line article-text-line width-85"></div>
      <div className="skeleton-line article-text-line width-60"></div>
    </div>
  );

  const renderTileBlockLoader = () => (
    <div className={cx(cssClasses, 'loader-wrapper responsive tile-block-grid')} style={{ ...config }}>
      {Array.from({ length: 4 }).map((_, index) => (
        <div className="tile-block-item" key={index}>
          <div className="skeleton-line tile-image-placeholder"></div>
          <div className="tile-text-content">
            <div className="skeleton-line tile-title-line"></div>
            <div className="skeleton-line tile-paragraph-line width-90"></div>
            <div className="skeleton-line tile-paragraph-line width-70"></div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderFeatureTileBlockLoader = () => (
    <div className={cx(cssClasses, 'loader-wrapper responsive feature-tile-block-grid')} style={{ ...config }}>
      {/* Left Column (Main Feature Tile) */}
      <div className="feature-tile-main-item">
        <div className="skeleton-line feature-image-placeholder"></div>
        <div className="feature-text-content">
          <div className="skeleton-line feature-title-line"></div>
          <div className="skeleton-line feature-paragraph-line width-90"></div>
          <div className="skeleton-line feature-paragraph-line width-70"></div>
        </div>
      </div>
      {/* Right Column (Grid of 4 smaller tiles) - Hidden on mobile */}
      <div className="feature-tile-sub-grid">
        {Array.from({ length: 4 }).map((_, index) => (
          <div className="feature-tile-sub-item" key={index}>
            <div className="skeleton-line feature-image-placeholder"></div>
            <div className="feature-text-content">
              <div className="skeleton-line feature-title-line"></div>
              <div className="skeleton-line feature-paragraph-line width-85"></div>
              <div className="skeleton-line feature-paragraph-line width-65"></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  switch (loaderType) {
    case 'Article':
      return renderArticleLoader();
    case 'TileBlock':
      return renderTileBlockLoader();
    case 'FeatureTileBlock':
      return renderFeatureTileBlockLoader();
    default:
      return null;
  }
};

export default SkeletonLoader;